import { useEffect, useState } from 'react';
import { db } from '../firebase';
import { collection, query, where, getDocs, writeBatch, Timestamp } from 'firebase/firestore';

interface UseExpiredReservationsReturn {
  openExpiredReservations: () => Promise<number>;
  isChecking: boolean;
  error: string | null;
}

/**
 * Hook joka tarkistaa ja avaa vanhentuneet PENDING-varaukset.
 * 
 * Korvaa scheduled Cloud Functionin toiminnallisuuden.
 * Käytetään vain admin-sivulla, jotta vain admineilla on oikeus
 * vapauttaa jumiutuneita varauksia.
 * 
 * Tarkistaa kaikki days-dokumentit joissa:
 * - status === 'PENDING'
 * - expires_at < nykyinen aika
 * 
 * Ja muuttaa niiden statuksen takaisin 'OPEN':iksi.
 */
export const useExpiredReservations = (): UseExpiredReservationsReturn => {
  const [isChecking, setIsChecking] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Tarkistaa ja avaa kaikki vanhentuneet PENDING-varaukset
   * @returns Avattujen varausten määrä
   */
  const openExpiredReservations = async (): Promise<number> => {
    setIsChecking(true);
    setError(null);
    
    try {
      const now = Timestamp.now();
      // Lisätään 30 sekunnin turvamarginaali varmistamaan että varaus on todella vanhentunut
      // Alkuperäinen expires_at on 60 sekuntia, joten tämä antaa 30s lisäaikaa
      const safetyMarginMs = 30 * 1000; // 30 sekuntia
      const safeTimestamp = Timestamp.fromMillis(now.toMillis() - safetyMarginMs);
      
      // Hae kaikki PENDING-tilassa olevat päivät joiden expires_at on mennyt (turvamarginaalilla)
      const expiredQuery = query(
        collection(db, 'days'),
        where('status', '==', 'PENDING'),
        where('expires_at', '<', safeTimestamp)
      );

      const snapshot = await getDocs(expiredQuery);

      if (snapshot.empty) {
        setIsChecking(false);
        return 0;
      }

      // Käytä batched writes tehokkuuden vuoksi (max 500 per batch)
      const BATCH_SIZE = 500;
      let totalOpened = 0;

      for (let i = 0; i < snapshot.docs.length; i += BATCH_SIZE) {
        const batch = writeBatch(db);
        const batchDocs = snapshot.docs.slice(i, i + BATCH_SIZE);

        batchDocs.forEach((doc) => {
          batch.update(doc.ref, {
            status: 'OPEN',
            token: null,
            expires_at: null,
            reserved_at: null,
            game_id: null,
          });
        });

        await batch.commit();
        totalOpened += batchDocs.length;
      }

      console.log(`Opened ${totalOpened} expired PENDING reservations`);
      setIsChecking(false);
      return totalOpened;

    } catch (err: any) {
      const errorMessage = err.message || 'Failed to open expired reservations';
      setError(errorMessage);
      console.error('Error opening expired reservations:', err);
      setIsChecking(false);
      throw new Error(errorMessage);
    }
  };

  return {
    openExpiredReservations,
    isChecking,
    error,
  };
};

/**
 * Hook joka automaattisesti tarkistaa vanhentuneet varaukset
 * komponentin mountatessa.
 * 
 * @param enabled - Onko tarkistus käytössä
 */
export const useAutoCheckExpiredReservations = (enabled: boolean = true) => {
  const { openExpiredReservations, isChecking, error } = useExpiredReservations();
  const [checked, setChecked] = useState<boolean>(false);

  useEffect(() => {
    if (enabled && !checked && !isChecking) {
      openExpiredReservations()
        .then((count) => {
          if (count > 0) {
            console.log(`Auto-opened ${count} expired reservations`);
          }
          setChecked(true);
        })
        .catch((err) => {
          console.error('Auto-check failed:', err);
          setChecked(true); // Merkitään kuitenkin tarkistetuksi ettei loopata
        });
    }
  }, [enabled, checked, isChecking, openExpiredReservations]);

  return { isChecking, error, checked };
};
